import { useTranslation } from 'react-i18next';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Card, Button, Typography, List, Skeleton } from 'antd';
import PropTypes from 'prop-types';
import { routes } from '../../../../utils/constants/ownerPensionerRoutes';
import { useSocketContext } from '../../../../contexts/SocketContext';
import { profileLockMessage } from '../../utils/profileLockMessage';
import { editingLocked } from '../../../../utils/editingLocked';
import { formatPhoneNumber } from '../../../../utils/formatPhoneNumber';

const { Text } = Typography;

/**
 * Renders a card component for displaying veterinarian information.
 * @function
 *
 * @param {Object} props - The component props.
 * @param {Object} props.veterinarian - The veterinarian object.
 * @param {Function} props.navigate - The function to navigate to a different page.
 * @param {Object} props.owner - The owner object.
 * @param {boolean} props.isFieldsLoading - Indicates if the fields are loading.
 * @returns {JSX.Element} The rendered VeterinarianCard component.
 */
export const VeterinarianCard = ({
  veterinarian,
  navigate,
  owner,
  isFieldsLoading
}) => {
  const { t } = useTranslation();
  const { profileLock } = useSocketContext();

  const createPath = `${routes?.VETERINARIAN_GROOMER}/create/veterinarian/${owner?._id}`;
  const editPath = `${routes?.VETERINARIAN_GROOMER}/edit/veterinarian/${veterinarian?._id}`;

  const items = veterinarian
    ? [
        {
          label: t('veterinarians.form.name'),
          content: veterinarian.name || '-'
        },
        {
          label: t('veterinarians.postal_address'),
          content: veterinarian.address
            ? `${veterinarian?.address?.number || ''} ${
                veterinarian?.address?.street || ''
              }`
            : '-'
        },
        {
          label: t('veterinarians.form.address.city'),
          content: veterinarian.address ? veterinarian?.address?.city : '-'
        },
        {
          label: t('veterinarians.form.address.country'),
          content: veterinarian.address ? veterinarian?.address?.country : '-'
        },
        {
          label: t('veterinarians.form.phone_number.number'),
          content: veterinarian?.phone_number?.number
            ? formatPhoneNumber(veterinarian.phone_number)
            : '-'
        }
      ]
    : [];

  return (
    <Card
      title={t('veterinarians.header_panel')}
      className="vet-card"
      extra={
        <Button
          type="primary"
          onClick={() => {
            if (editingLocked(profileLock, owner?._id)) {
              profileLockMessage(t, 'profile');
              return;
            }
            navigate(`${veterinarian ? editPath : createPath}`);
          }}
        >
          {veterinarian ? <EditOutlined /> : <PlusOutlined />}
          {veterinarian ? t('buttons.edit') : t('buttons.create')}
        </Button>
      }
    >
      {veterinarian ? (
        <Skeleton loading={isFieldsLoading} active>
          <List
            itemLayout="vertical"
            dataSource={items}
            renderItem={(item) => (
              <List.Item>
                <Text>{item.label} : </Text>
                <Text strong>{item.content}</Text>
              </List.Item>
            )}
          />
        </Skeleton>
      ) : (
        <Text>{t('veterinarians.no_veterinarian')}</Text>
      )}
    </Card>
  );
};

VeterinarianCard.propTypes = {
  veterinarian: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    address: PropTypes.shape({
      number: PropTypes.string,
      street: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string
    }),
    phone_number: PropTypes.shape({
      country_code: PropTypes.string,
      number: PropTypes.string
    })
  }),
  navigate: PropTypes.func.isRequired,
  owner: PropTypes.shape({
    _id: PropTypes.string
  }).isRequired,
  isFieldsLoading: PropTypes.bool.isRequired
};

VeterinarianCard.defaultProps = {
  veterinarian: null
};
